import React, { useCallback, useEffect, useState } from 'react';
import { LogOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { adminGet } from './adminApi';
import { ADMIN_TABS } from './adminConfig';
import { AdminRecordList } from './AdminRecordList';

export const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState(ADMIN_TABS[0].key);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(ADMIN_TABS.map((tab) => adminGet(tab.path, token)));
      setData(Object.fromEntries(ADMIN_TABS.map((tab, i) => [tab.key, results[i]])));
    } catch (err) {
      toast.error(err.message);
      if (err.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const tab = ADMIN_TABS.find((t) => t.key === activeTab);

  return (
    <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8 sm:py-14" data-testid="admin-dashboard">
      <header className="mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">3D Printer Gear admin</p>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Enquiries</h1>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={load} disabled={loading} className="flex items-center gap-2 border border-gray-900 bg-white px-5 py-3 text-sm font-bold transition-colors hover:bg-gray-900 hover:text-white disabled:opacity-60" data-testid="admin-refresh-button">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Refresh
          </button>
          <button type="button" onClick={onLogout} className="flex items-center gap-2 bg-gray-900 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-black" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" aria-hidden="true" /> Log out
          </button>
        </div>
      </header>
      <nav className="mb-8 flex flex-wrap border-b border-gray-300" aria-label="Admin sections">
        {ADMIN_TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setActiveTab(t.key)} className={`-mb-px border-b-2 px-4 py-3 text-sm font-bold transition-colors ${activeTab === t.key ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-testid={`admin-tab-${t.key}`}>
            {t.label} <span className="ml-1 bg-gray-200 px-2 py-0.5 text-xs" data-testid={`admin-tab-count-${t.key}`}>{data[t.key]?.length ?? '–'}</span>
          </button>
        ))}
      </nav>
      <AdminRecordList key={tab.key} tab={tab} records={data[tab.key] || []} loading={loading} />
    </main>
  );
};
